import React from "react";
import * as R from "ramda";
import { compose, branch, renderComponent } from "recompose";
import { Switch, Route, Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import qs from "qs";
import { dispatchOnMount, spinWhile } from "@worcket/common/hocs";
import { createGlobalStyle } from "styled-components";

import SignUp from "../scenes/SignUp";
import {
  PasswordRecoveryEmail,
  PasswordRecoveryChange
} from "../scenes/PasswordRecovery";
import Linkedin from "../scenes/Linkedin";
import Login from "../scenes/Login";
import User from "../scenes/User";
import Company from "../scenes/Company";
import Opportunity from "../scenes/Opportunity";
import Opportunities from "../scenes/Opportunities";
import NewOpportunity from "../scenes/Opportunity/NewOpportunity";
import Companies from "../scenes/Companies";
import NewTeam from "../scenes/Teams/Create";
import Team from "../scenes/Teams/Team";
import Analytics from "../scenes/Analytics";
import { fetchInitialData, FETCH_INITIAL_DATA } from "../store/actions/session";
import ErrorBoundary from "../scenes/ErrorBoundary";
import { useClientConnect } from "../utils/useClientConnect";
import CandidatesBase from "../scenes/CandidatesBase/CandidatesBase";
import { canCreateOpportunity } from "../store/selectors/user";

import SkeletonApp from "./SkeletonApp";
import Layout from "./Layout";
// import EnvironmentSelector from "./EnvironmentSelector";

const GlobalStyles = createGlobalStyle`
  #HW_badge {
    background: ${({ theme }) => theme["@alert"]};
    font-size: 12px;
  }
`;

const AuthRoutes = ({ history }) => {
  const pathname = R.pathOr("", ["location", "pathname"], history);

  return (
    <>
      <GlobalStyles />
      <Switch>
        <Route path="/signup" component={SignUp} />
        <Route path="/olvide-mi-contraseña" component={PasswordRecoveryEmail} />
        <Route path="/cambiar-contraseña" component={PasswordRecoveryChange} />
        <Route path="/login" component={Login} />
        <Redirect to={`/login?${qs.stringify({ redirect_to: pathname })}`} />
      </Switch>
      {/* {process.env.REACT_APP_ENVIRONMENT !== "PROD" && <EnvironmentSelector />} */}
    </>
  );
};

const App = ({
  location,
  isSuperadmin,
  isAdmin,
  company,
  headquarter,
  user,
  userConfig
}) => {
  useClientConnect(company.id);

  const redirectTo = R.pipe(
    search => qs.parse(search, { ignoreQueryPrefix: true }),
    R.propOr(isSuperadmin ? "/compañias" : "/oportunidades", "redirect_to")
  )(location.search);

  return (
    <ErrorBoundary>
      <Layout>
        <Switch>
          <Route path="/linkedin" component={Linkedin} />
          <Route path="/usuario" render={props => <User {...props} />} />
          {isSuperadmin && <Route path="/compañias" component={Companies} />}
          {isSuperadmin && (
            <Route
              path="/cuartel"
              render={props => (
                <Company
                  isHeadquarter={true}
                  key={headquarter.id}
                  {...props}
                  company={headquarter}
                />
              )}
            />
          )}
          {isSuperadmin && company.id && (
            <Route
              path="/(compañia|compania)"
              render={props => (
                <Company key={company.id} {...props} company={company} />
              )}
            />
          )}
          {(isSuperadmin || isAdmin) && (
            <Route
              path="/analytics"
              render={props => (
                <Analytics
                  {...props}
                  company={company || headquarter}
                  showTitle={true}
                />
              )}
            />
          )}
          {isSuperadmin && !company.id && <Redirect to="/compañias" />}
          <Route
            path="/(compañia|compania)"
            render={props => <Company {...props} company={company} />}
          />
          <Route
            path="/oportunidades/nueva"
            render={props =>
              canCreateOpportunity(user) ? (
                <NewOpportunity {...props} />
              ) : (
                <Redirect to="/oportunidades" />
              )
            }
          />
          <Route
            path="/oportunidades/:id/:category?"
            render={props => (
              <Opportunity {...props} key={props.match.params.id} />
            )}
          />
          <Route path="/oportunidades" component={Opportunities} />
          {R.pathOr(true, ["candidatesBase"], userConfig) && (
            <Route path="/base-candidatos" component={CandidatesBase} />
          )}
          <Route
            path="/equipos/nuevo"
            render={props =>
              canCreateOpportunity(user) ? (
                <NewTeam {...props} />
              ) : (
                <Redirect to="/oportunidades" />
              )
            }
          />
          <Route
            path="/equipos/:teamId/:category?"
            render={props => <Team {...props} key={props.match.params.id} />}
          />
          <Redirect to={redirectTo} />
        </Switch>
        {/* {process.env.REACT_APP_ENVIRONMENT !== "PROD" && (
          <EnvironmentSelector />
        )} */}
      </Layout>
    </ErrorBoundary>
  );
};

const enhancer = compose(
  withRouter,
  connect(
    R.applySpec({
      token: R.path(["session", "token"]),
      company: R.propOr({}, "company"),
      headquarter: R.propOr({}, "headquarter"),
      isSuperadmin: R.pipe(
        R.pathOr([], ["user", "roles"]),
        R.includes("headadmin")
      ),
      isAdmin: R.pipe(
        R.pathOr([], ["user", "roles"]),
        R.includes("admin")
      ),
      user: R.propOr({}, "user"),
      userConfig: R.pathOr({}, ["user", "config"])
    })
  ),
  branch(({ token }) => !token, renderComponent(AuthRoutes)),
  dispatchOnMount(fetchInitialData),
  spinWhile(FETCH_INITIAL_DATA, { initial: true }, <SkeletonApp />)
);

export default enhancer(App);
